/**
 * Google Consent Mode v2. GTM loads on every visit; these signals tell it what
 * the visitor allowed, and each tag in the container decides from them whether
 * it may fire. Which scripts run is configured in GTM, not here.
 *
 * Each banner category maps to the Consent Mode types it governs:
 */
const CONSENT_TYPES = {
  analytics: ['analytics_storage'],
  ad: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  functionality: ['functionality_storage'],
  personalization: ['personalization_storage'],
} as const;

type ConsentValue = 'granted' | 'denied';

/** Set by the consent manager (`@almacareer/cookie-consent-manager`). */
const CONSENT_COOKIE = 'almacareer_ccm';

/**
 * How long GTM holds tags for a consent update before firing them under the
 * defaults. Covers the consent manager starting up on a first visit; a
 * returning visitor's saved choice is already in the defaults.
 */
const WAIT_FOR_UPDATE_MS = 500;

/**
 * Consent Mode commands must reach the dataLayer as an `arguments` object; GTM
 * ignores the same values pushed as a plain array.
 */
function gtag(..._args: unknown[]): void {
  window.dataLayer = window.dataLayer ?? [];
  // eslint-disable-next-line prefer-rest-params -- see above.
  window.dataLayer.push(arguments);
}

export function consentState(categories: readonly string[]): Record<string, ConsentValue> {
  const state: Record<string, ConsentValue> = {
    // Strictly necessary cookies need no consent.
    security_storage: 'granted',
  };

  for (const [category, types] of Object.entries(CONSENT_TYPES)) {
    for (const type of types) {
      state[type] = categories.includes(category) ? 'granted' : 'denied';
    }
  }

  return state;
}

/**
 * The categories a returning visitor accepted, read straight from the consent
 * cookie. The consent manager starts only once the fair (and so the banner's
 * language) has loaded, which is too late for the defaults GTM needs before its
 * first tag. Anything unreadable counts as no consent.
 */
export function readSavedCategories(cookies: string = document.cookie): string[] {
  const raw = cookies
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_COOKIE}=`))
    ?.slice(CONSENT_COOKIE.length + 1);

  if (raw === undefined || raw === '') {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    const categories: unknown =
      typeof parsed === 'object' && parsed !== null && 'categories' in parsed
        ? parsed.categories
        : undefined;

    return Array.isArray(categories)
      ? categories.filter((category): category is string => typeof category === 'string')
      : [];
  } catch {
    return [];
  }
}

/** Must run before GTM loads, so no tag fires without a consent state. */
export function setDefaultConsent(categories: readonly string[]): void {
  gtag('consent', 'default', { ...consentState(categories), wait_for_update: WAIT_FOR_UPDATE_MS });
}

export function updateConsent(categories: readonly string[]): void {
  gtag('consent', 'update', consentState(categories));
}
